// Shared registration-period label formatter.
//
// Every surface (public class card, registration modal, calendar, admin class
// editor, RegistrationPeriodsManager) must use `formatPeriodLabel()` so labels
// never drift. Never render "Summer · August", "Fall · September", or any
// concatenated internal category.
//
// Repaired `display_label` from earlier cleanup remains a safe fallback while
// the `period_type` backfill rolls out.

export type PeriodType = "monthly" | "fall_semester" | "spring_semester" | "custom";

export interface PeriodShape {
  name?: string | null;
  display_label?: string | null;
  period_type?: PeriodType | string | null;
  month_number?: number | null;
  year?: number | null;
  academic_year?: string | null;
  semester?: string | null;
  start_date?: string | null;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function monthName(n: number | null | undefined): string | null {
  if (!n || n < 1 || n > 12) return null;
  return MONTH_NAMES[n - 1];
}

/**
 * Canonical parent-facing label for a registration period.
 *
 * Rules:
 *   monthly         → "May"                  (bare month; no "Summer ·" prefix)
 *   fall_semester   → "Fall Semester"
 *   spring_semester → "Spring Semester"
 *   custom          → display_label || name
 *
 * Falls back through display_label → name → "Registration Period".
 */
export function formatPeriodLabel(p: PeriodShape | null | undefined): string {
  if (!p) return "Registration Period";

  const type = (p.period_type ?? null) as PeriodType | null;

  if (type === "monthly") {
    const m = monthName(p.month_number ?? null);
    if (m) return m;
  }
  if (type === "fall_semester") return "Fall Semester";
  if (type === "spring_semester") return "Spring Semester";

  // Fallback path (custom rows, or rows not yet backfilled): trust the
  // repaired display_label, then name. Strip any stray "Summer · " / "Fall · "
  // / "Spring · " prefixes so a stale row can never leak the old format.
  const raw = (p.display_label && p.display_label.trim()) || (p.name && p.name.trim()) || "";
  const cleaned = raw.replace(/^(Summer|Fall|Spring)\s*[·•|:-]\s*/i, "").trim();
  return cleaned || "Registration Period";
}

/**
 * Short suffix for CTA text, e.g. "Notify Me · August".
 * Callers still own the verb and separator; this only returns the label.
 */
export function periodCtaSuffix(p: PeriodShape | null | undefined): string {
  return formatPeriodLabel(p);
}
